import axios,  {type AxiosRequestConfig, type AxiosResponse  } from 'axios';
import {API_ENDPOINTS} from "@/global/api/config/api-endpoints";
import {ConstService} from "@/service/ConstService";
import router from "@/router";
import AuthService from "@/service/auth/AuthService";
import { ErrorHandler, errorMessages } from '@/global/api/handler/ErrorHandler'
import {useAuthStore} from "@/stores/authStore";

// 기본 API 설정
export const defaultApiClient = axios.create({
    baseURL: API_ENDPOINTS.BASE,
    withCredentials: false,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
    }
});

// 요청 인터셉터
defaultApiClient.interceptors.request.use(
    (config) => {
        // 토큰이 필요한 경우 헤더에 추가
        const token = localStorage.getItem(ConstService.localStorage.ACCESS_TOKEN);

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 응답 인터셉터
defaultApiClient.interceptors.response.use(
    (response) => {

        return response.data;
    },
    async (error) => {
        const originalRequest = error.config;
        const token = AuthService.getToken();
        const authStore = useAuthStore();
        const status = error.response?.status
        const errorMessage = errorMessages[status] || errorMessages.default
/*        console.log('response                      : ' + status);
        console.log('AuthService.IS_PROCESS_LOGOUT : ' + AuthService.IS_PROCESS_LOGOUT);
        console.log('authStore.isLoggedIn          : ' + authStore.isLoggedIn);
        console.log('authStore.isLoggingOut        : ' + authStore.isLoggingOut);
        console.log('originalRequest._retry        : ' + originalRequest._retry);*/
        // 상태 코드별 다른 처리
        switch (status) {
            case 401:
                // 1. 이미 로그아웃 처리 중인 경우
                if (AuthService.IS_PROCESS_LOGOUT) {
                    authStore.finishLogout();
                    await router.push({ name: 'login' });
                    return Promise.reject(error);
                }

                // 2. 로그인하지 않은 상태에서의 401 에러
                if (!authStore.isLoggedIn && !authStore.isLoggingOut) {
                    ErrorHandler.showErrorModal('Notification', error.response.data.error.details);
                    await router.push({ name: 'login' });
                    break;
                    // return Promise.reject(error);
                }

                // 3. 토큰 리프레시 재시도 방지
                if (originalRequest._retry) {
                    await AuthService.handleLogout(router);
                    return Promise.reject(error);
                }

                // 4. 토큰 리프레시 시도
                originalRequest._retry = true;
                try {
                    await AuthService.getTokenManager().handleTokenRefresh(
                        originalRequest,
                        () => AuthService.refreshAccessToken(),
                        async () => {
                            console.log('refresh fail');
                            await AuthService.handleLogout(router);
                        }
                    );
                    return defaultApiClient(originalRequest);
                } catch (refreshError) {
                    await AuthService.handleLogout(router);
                    return Promise.reject(refreshError);
                }
            case 403:
                // 권한 에러도 모달로 표시
                ErrorHandler.showErrorModal(error.response.data.error.title, error.response.data.error.details)
                // 현재 유저의 role 가져오기
                const currentUser = AuthService.getUser();
                console.log('currentUser : ', currentUser);
                const userRole = currentUser?.roleNm;

                // role에 따른 페이지 라우팅
                switch(userRole) {
                    case 'ADMIN':
                        await router.push('/admin-forbidden');
                        break;
                    case 'MANAGER':
                        await router.push('/manager-forbidden');
                        break;
                    case 'USER':
                        await router.push('/user-forbidden');
                        break;
                    case 'PENDING':
                        await router.push({ name: 'email-verification' , query: {emlAddr: currentUser.emlAddr}});
                        break;
                    default:
                        await router.push('/forbidden'); // 기본 forbidden 페이지
                }
                break;
            case 400:
                ErrorHandler.showErrorModal(error.response.data.error.title, error.response.data.error.details)
                break
            case 500:
                ErrorHandler.showErrorModal(ConstService.ERR.TITLE.SYSTEM_ERROR, error.response.data.error.title + ConstService.BR_TAG + error.response.data.error.details)
                break
            default:
                ErrorHandler.showToast(errorMessage)
        }

        return Promise.reject(error);
    }
);

// defaultAPI 함수
export const defaultAPI = async <T = any>(config: AxiosRequestConfig): Promise<T> => {
    try {
        const response: AxiosResponse<T> = await defaultApiClient(config);
        return response.data; // response.data를 반환
    } catch (error) {
        throw error;
    }
};

/**
 // 사용할 때
 const products = await defaultAPI<Product[]>({
 url: '/products',
 method: 'GET'
 });
 */